import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import {
  Error as ErrorIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { TextCallout } from '@redshelf/redshelf-ui';

const useStyles = makeStyles((theme) => ({
  root: {
    alignItems: 'flex-start',
    background: ({ displayError }) =>
      displayError ? 'rgba(211, 47, 47, 0.08)' : '#F9F9F9',
    border: ({ displayError }) =>
      displayError ? '2px solid #D32F2F' : '1px solid #E5E5E5',
    lineHeight: theme.typography.pxToRem(21),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 1, 1.5, 1.25),
  },
  icon: {
    color: ({ displayError }) => (displayError ? '#D32F2F' : '#394551'),
    paddingTop: 0,
  },
}));

/**
 * Creates an element with the correct text content based on menu context.
 * Displayed in UsagedBar, and used directly by UsageBarAlert.
 *
 * @param {Boolean} error - should this element use the error state text?
 * @param {String} menu - string that indicates which menu is using this element
 * @param {Number} percent - value that represents the available DRM percentage
 * @returns
 */
const getText = (error, menu, percent) => {
  let action = '';
  let errorInfo = '';
  let info = '';

  switch (menu) {
    case 'copy':
      info = ' of this content';
      action = 'the content';
      errorInfo = 'the copy allowance set by the publisher';
      break;
    case 'download':
      action = 'the content to view offline';
      break;
    case 'print':
      info = ' of this content';
      action = 'this content to a PDF';
      errorInfo = 'your print allowance and cannot be printed to a PDF';
      break;
    default:
      break;
  }

  return (
    <p>
      {error ? (
        `Your current selection exceeds ${errorInfo}.`
      ) : (
        <>
          {`The publisher${info} allows you to ${menu} up to`}
          <b>
            <span className="noTranslate">{` ${(percent * 100).toFixed(
              1,
            )}%`}</span>
            {` of ${action}.`}
          </b>
        </>
      )}
    </p>
  );
};

const UsageBarAlert = ({ alert, percent, ...props }) => {
  const displayError = alert?.isError ?? false;
  const classes = useStyles({ displayError });
  return (
    <TextCallout
      classes={{ root: classes.root, icon: classes.icon }}
      icon={displayError ? <ErrorIcon /> : <InfoIcon />}
      tabIndex={null} // resolves an accessbility violation
      // do not set a tabindex when we update this component to Talon or MUI5
      {...props}
    >
      {getText(displayError, alert.menu, percent)}
    </TextCallout>
  );
};

UsageBarAlert.propTypes = {
  alert: PropTypes.shape({
    /**
     * Should the alert display an error state
     */
    isError: PropTypes.bool,
    /**
     * Menu context, used to return the correct text from `getText` function
     */
    menu: PropTypes.string.isRequired,
  }).isRequired,
  /**
   * Percent of DRM available, in decimal format
   */
  percent: PropTypes.number.isRequired,
};

export default UsageBarAlert;
